import { getOperatingSystem, isDefined } from "@dashboard/library";
import { Maybe } from "graphql/jsutils/Maybe";
import Cookies from "js-cookie";
import { snakeCase } from "snake-case";
import { COOKIE_KEYS } from "@/constants/auth.constants";
import { APP_CONFIG } from "@/constants/config.constants";
import { tracker } from "@/constants/tracker.constants";
import {
  CallTranscriptionSettings,
  VoicemailTranscriptionSettings,
} from "@/graphql/generated/graphql";
import { TrackFunctionParams } from "@/hooks/useTracker/useTracker.decl";
import { Call as CustomCall } from "@/states/call/call.utility.decl";
import {
  TranscriptionLanguageFormatted,
  TranscriptionLanguagesSupported,
} from "./tracker.helpers.decl";

export interface CommonPayload {
  platform: string;
  release: string;
  screen_size: string;
  window_size: string;
  os_version: string;
}

export interface SignedInAsPayload {
  aircall_employee_signed_in_as: boolean;
}

export interface GetTrackSettingsPropertiesParams {
  event: string;
  settingsSection: string;
  setting: string;
  field?: string;
  action?: string;
  isOwnUser?: boolean;
}

/**
 * Gets the applocation context.
 * @returns object holding the current application's context
 */
export const getCommonPayload = (): CommonPayload => ({
  platform: APP_CONFIG.device,
  release: APP_CONFIG.release,
  screen_size: `${window.screen.width}x${window.screen.height}`,
  window_size: `${window.outerWidth}x${window.outerHeight}`,
  os_version: getOperatingSystem().osVersion,
});

/**
 * Gets the applocation context.
 * @returns object holding the current application context
 */
export const getSignedInAsPayload = (): SignedInAsPayload => ({
  aircall_employee_signed_in_as:
    Cookies.get(COOKIE_KEYS.IS_CONNECTED_AS) === "true",
});

/**
 * Gets formatted setting properties to be tracked.
 * @param getTrackSettingsPropertiesParams - destructured parameters
 * @returns object holding tracking properties
 */
export const getTrackSettingsProperties = ({
  event,
  settingsSection,
  setting,
  action,
  isOwnUser,
  field,
}: GetTrackSettingsPropertiesParams): TrackFunctionParams => ({
  event,
  payload: {
    ...(isDefined(isOwnUser) && {
      target_user: isOwnUser ? "self" : "other_user",
    }),
    ...(isDefined(field)
      ? { action: `${snakeCase(field)}_${action || "updated"}` }
      : { action }),
    settings_section: settingsSection,
    setting,
  },
});

/**
 * Sends tracker event.
 * @param eventName - event name.
 * @param payload - data payload.
 * @returns void
 */
export function sendEvent<PayloadType extends object>(
  eventName: string,
  payload: PayloadType
): void {
  return tracker.track(eventName, payload);
}

/**
 * Formats supported IETF language tag to the language's name.
 * @param lang - supported IETF language tag.
 * @returns formatted IETF language tag.
 */
export function languageMapper(
  lang: TranscriptionLanguagesSupported
): TranscriptionLanguageFormatted {
  const mapper: Record<
    TranscriptionLanguagesSupported,
    TranscriptionLanguageFormatted
  > = {
    "en-US": "English",
    "fr-FR": "French",
  };

  return mapper[lang];
}

export function pickReason(
  call?: Maybe<CustomCall>,
  transcriptionSettings?:
    | CallTranscriptionSettings
    | VoicemailTranscriptionSettings
    | null
) {
  if (!call?.transcription) {
    return "transcriptionDisabled";
  }

  if (!call?.transcription && (call.durationInSecond ?? Infinity) < 60) {
    return "call is less than 1 minute";
  }

  if (!call?.transcription.status) {
    return "undefined";
  }

  if (call?.transcription.status !== "available") {
    return call.transcription.status;
  }

  if (!transcriptionSettings) {
    return null;
  }

  if (!transcriptionSettings.reason && !transcriptionSettings.isEnabled) {
    return "Transcription settings for the line are not enabled.";
  }

  if (call?.transcription.status === "available") {
    return null;
  }

  return null;
}
